import { Tooltip } from "primereact/tooltip";
import React, { useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import type { ThunkDispatch } from "redux-thunk";
import * as XLSX from "xlsx";

import type { BulkCreateUserItem } from "../../api";
import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import { accountUsersActions } from "../../reducers/accountUsers";
import {
  validateUsernamePassword,
  validateUserDisplayName,
  validateEmail,
  validateRole,
  validateCreditLimit,
  VALID_ROLES,
} from "../../utils/validators";
import {
  CheckCircleIcon,
  CloseModalIcon,
  ErrorInfoIcon,
  InfoIcon,
  SelectFileIcon,
  WarningIcon,
  NoticeBulkLoginIdAndPasswordIcon,
  NoticeBulkUserNameIcon,
  NoticeBulkEmailIcon,
  NoticeBulkRoleIcon,
  NoticeBulkCreditLimitIcon,
} from "../SVGIcons";

import UserDashboardBox from "./UserDashboardBox";

// ============================================================
// 型定義
// ============================================================

/** ユーザーデータ */
export interface BulkUserData {
  rowNumber: number;
  loginId: string;
  password: string;
  userName: string;
  email: string;
  role: string;
  creditLimit: number | null;
}

/** フロントバリデーションエラー */
interface UserValidationErrors {
  loginId: string;
  password: string;
  userName: string;
  email: string;
  role: string;
  creditLimit: string;
}

/** APIエラー（値ベース） */
interface ApiFieldError {
  value: string | number | null;
  error: string;
}

interface ApiValidationErrors {
  loginId?: ApiFieldError;
  password?: ApiFieldError;
  userName?: ApiFieldError;
  email?: ApiFieldError;
  role?: ApiFieldError;
  creditLimit?: ApiFieldError;
}

/** 各フィールドのエラー（フロント+API統合後） */
interface FieldErrors {
  loginId: string | undefined;
  password: string | undefined;
  userName: string | undefined;
  email: string | undefined;
  role: string | undefined;
  creditLimit: string | undefined;
}

/** 進捗情報 */
export interface BulkCreateProgress {
  current: number;
  total: number;
}

// ============================================================
// 定数
// ============================================================

const ALLOWED_EXTENSIONS = [".xlsx", ".csv"];
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const CHUNK_SIZE = 10;
const SECONDS_PER_USER = 0.7;

/** 権限の日本語表記からAPI用の値に変換 */
const ROLE_MAP: Record<string, "Admin" | "General" | "Viewer"> = {
  管理者ユーザー: "Admin",
  一般ユーザー: "General",
  閲覧ユーザー: "Viewer",
};

/** ヘッダー列の定義 */
const HEADER_COLUMNS = {
  NO: { index: 0, label: "No", icon: null },
  LOGIN_ID: {
    index: 1,
    label: "ログインID",
    icon: <NoticeBulkLoginIdAndPasswordIcon />,
  },
  PASSWORD: {
    index: 2,
    label: "パスワード",
    icon: <NoticeBulkLoginIdAndPasswordIcon />,
  },
  USER_NAME: {
    index: 3,
    label: "ユーザー名",
    icon: <NoticeBulkUserNameIcon />,
  },
  EMAIL: { index: 4, label: "メールアドレス", icon: <NoticeBulkEmailIcon /> },
  ROLE: { index: 5, label: "権限", icon: <NoticeBulkRoleIcon /> },
  CREDIT_LIMIT: {
    index: 6,
    label: "個別上限",
    icon: <NoticeBulkCreditLimitIcon />,
  },
};

const HEADER_KEYWORDS = Object.values(HEADER_COLUMNS).map((col) => col.label);

// ============================================================
// バリデーション関連
// ============================================================

/** ユーザーデータのバリデーション */
const validateUserData = (
  user: BulkUserData,
  duplicateLoginIds: Set<string>,
): UserValidationErrors => {
  let loginIdError = validateUsernamePassword(user.loginId, "ログインID");
  if (!loginIdError && duplicateLoginIds.has(user.loginId)) {
    loginIdError = "ログインIDが重複しています";
  }

  return {
    loginId: loginIdError,
    password: validateUsernamePassword(user.password, "パスワード"),
    userName: validateUserDisplayName(user.userName),
    email: validateEmail(user.email),
    role: validateRole(user.role),
    creditLimit: validateCreditLimit(user.creditLimit),
  };
};

/** エラーがあるかどうかをチェック */
const hasValidationErrors = (errors: UserValidationErrors): boolean =>
  Object.values(errors).some((error) => error !== "");

/** APIエラーを値ベースでチェック（値が変更されたらエラーを消す） */
const getApiError = (
  apiError: ApiFieldError | undefined,
  currentValue: string | number | null,
): string | undefined => {
  if (!apiError) return undefined;
  return apiError.value === currentValue ? apiError.error : undefined;
};

/** フロント+APIエラーを統合 */
const getFieldErrors = (
  errors: UserValidationErrors | undefined,
  apiValidationError: ApiValidationErrors | undefined,
  apiLoginIdError: { loginId: string; error: string } | null,
  currentUser: BulkUserData,
): FieldErrors => ({
  loginId:
    errors?.loginId ||
    getApiError(apiValidationError?.loginId, currentUser.loginId) ||
    (apiLoginIdError?.loginId === currentUser.loginId
      ? apiLoginIdError.error
      : undefined),
  password:
    errors?.password ||
    getApiError(apiValidationError?.password, currentUser.password),
  userName:
    errors?.userName ||
    getApiError(apiValidationError?.userName, currentUser.userName),
  email:
    errors?.email || getApiError(apiValidationError?.email, currentUser.email),
  role: errors?.role || getApiError(apiValidationError?.role, currentUser.role),
  creditLimit:
    errors?.creditLimit ||
    getApiError(apiValidationError?.creditLimit, currentUser.creditLimit),
});

/** エラーメッセージの配列を生成 */
const buildErrorMessages = (fieldErrors: FieldErrors): string[] => {
  const messages: string[] = [];
  if (fieldErrors.loginId)
    messages.push(`• ログインID: ${fieldErrors.loginId}`);
  if (fieldErrors.password)
    messages.push(`• パスワード: ${fieldErrors.password}`);
  if (fieldErrors.userName)
    messages.push(`• ユーザー名: ${fieldErrors.userName}`);
  if (fieldErrors.email)
    messages.push(`• メールアドレス: ${fieldErrors.email}`);
  if (fieldErrors.role) messages.push(`• 権限: ${fieldErrors.role}`);
  if (fieldErrors.creditLimit)
    messages.push(`• 個別上限: ${fieldErrors.creditLimit}`);
  return messages;
};

// ============================================================
// ファイルパース関連
// ============================================================

/** ヘッダー行を検出 */
const findHeaderRowIndex = (rows: (string | number | null)[][]): number => {
  for (const [i, row] of rows.entries()) {
    if (!row) continue; // eslint-disable-line no-continue
    const isHeaderMatch = HEADER_KEYWORDS.every((keyword, j) => {
      const cellValue = String(row[j] ?? "").trim();
      return cellValue === keyword;
    });
    if (isHeaderMatch) return i;
  }
  return -1;
};

/** 行データをユーザーデータに変換 */
const parseRowToUserData = (
  row: (string | number | null)[],
  rowNumber: number,
): BulkUserData | null => {
  const { LOGIN_ID, PASSWORD, USER_NAME, EMAIL, ROLE, CREDIT_LIMIT } =
    HEADER_COLUMNS;

  const loginId = String(row[LOGIN_ID.index] ?? "").trim();
  const password = String(row[PASSWORD.index] ?? "").trim();
  const userName = String(row[USER_NAME.index] ?? "").trim();
  const email = String(row[EMAIL.index] ?? "").trim();
  const role = String(row[ROLE.index] ?? "").trim();
  const creditLimitValue = row[CREDIT_LIMIT.index];

  const hasData =
    loginId || password || userName || email || role || creditLimitValue;
  if (!hasData) return null;

  return {
    rowNumber,
    loginId,
    password,
    userName,
    email,
    role,
    creditLimit:
      creditLimitValue !== null && creditLimitValue !== ""
        ? Number(creditLimitValue)
        : null,
  };
};

/** シートからユーザーデータを読み込む */
const parseSheetData = (
  rows: (string | number | null)[][],
): { users: BulkUserData[]; headerRowIndex: number } | null => {
  const headerRowIndex = findHeaderRowIndex(rows);
  if (headerRowIndex === -1) return null;

  const dataStartRow = headerRowIndex + 1;
  const users: BulkUserData[] = rows
    .slice(dataStartRow)
    .map((row, index) => {
      if (!row || row.every((cell) => cell === null || cell === ""))
        return null;
      return parseRowToUserData(row, dataStartRow + index + 1);
    })
    .filter((user): user is BulkUserData => user !== null);

  return { users, headerRowIndex };
};

/** 配列を指定サイズのチャンクに分割 */
const chunkArray = <T,>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

// ============================================================
// APIレスポンス処理
// ============================================================

/** バリデーションAPIレスポンスからエラーMapを生成 */
const buildValidationErrorMap = (
  failedInfos: Array<{
    row_index: number;
    user_name?: string;
    user_name_error?: string;
    password?: string;
    password_error?: string;
    full_name?: string;
    full_name_error?: string;
    email?: string;
    email_error?: string;
    role_type?: string;
    role_type_error?: string;
    credit_limit?: number | null;
    credit_limit_error?: string;
  }>,
  unregisteredUsers: Array<{ originalIndex: number }>,
): Map<number, ApiValidationErrors> => {
  const errorMap = new Map<number, ApiValidationErrors>();

  failedInfos.forEach((err) => {
    const originalIndex = unregisteredUsers[err.row_index]?.originalIndex;
    if (originalIndex === undefined) return;

    const apiError: ApiValidationErrors = {};
    if (err.user_name_error) {
      apiError.loginId = {
        value: err.user_name ?? "",
        error: err.user_name_error,
      };
    }
    if (err.password_error) {
      apiError.password = {
        value: err.password ?? "",
        error: err.password_error,
      };
    }
    if (err.full_name_error) {
      apiError.userName = {
        value: err.full_name ?? "",
        error: err.full_name_error,
      };
    }
    if (err.email_error) {
      apiError.email = { value: err.email ?? "", error: err.email_error };
    }
    if (err.role_type_error) {
      apiError.role = {
        value: err.role_type ?? "",
        error: err.role_type_error,
      };
    }
    if (err.credit_limit_error) {
      apiError.creditLimit = {
        value: err.credit_limit ?? null,
        error: err.credit_limit_error,
      };
    }
    errorMap.set(originalIndex, apiError);
  });

  return errorMap;
};

/** BulkUserDataをAPI用の形式に変換 */
const convertToApiUsers = (users: BulkUserData[]): BulkCreateUserItem[] =>
  users.map((user) => ({
    user_name: user.loginId,
    password: user.password,
    full_name: user.userName,
    email: user.email,
    role_type: ROLE_MAP[user.role] || "General",
    credit_limit: user.creditLimit,
  }));

// ============================================================
// UIコンポーネント
// ============================================================

interface EditableCellProps {
  error: string | undefined;
  value: string;
  onChange: (v: string) => void; // eslint-disable-line no-unused-vars
  type?: "text" | "email";
  className?: string;
  style?: React.CSSProperties;
}

const EditableCell: React.FC<EditableCellProps> = ({
  error,
  value,
  onChange,
  type = "text",
  className = "",
  style,
}) => (
  <td
    className={
      error ? "border border-gray-300 bg-red-100" : "border border-gray-300"
    }
    title={error || undefined}
    style={style}
  >
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full h-full px-2 py-1 bg-transparent border-none focus:outline-none ${className}`}
    />
  </td>
);

interface SelectCellProps {
  error: string | undefined;
  value: string;
  onChange: (v: string) => void; // eslint-disable-line no-unused-vars
  options: string[];
}

const SelectCell: React.FC<SelectCellProps> = ({
  error,
  value,
  onChange,
  options,
}) => (
  <td
    className={
      error ? "border border-gray-300 bg-red-100" : "border border-gray-300"
    }
    title={error || undefined}
  >
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-full px-2 py-1 bg-transparent border-none focus:outline-none cursor-pointer"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </td>
);

/** ユーザーテーブル行コンポーネント */
interface UserTableRowProps {
  user: BulkUserData;
  index: number;
  fieldErrors: FieldErrors;
  hasError: boolean;
  onUpdate: (f: keyof BulkUserData, v: string | number | null) => void; // eslint-disable-line no-unused-vars
}

const UserTableRow: React.FC<UserTableRowProps> = ({
  user,
  index,
  fieldErrors,
  hasError,
  onUpdate,
}) => {
  const creditLimitDisplay =
    user.creditLimit === null
      ? ""
      : Number.isNaN(user.creditLimit)
        ? "?"
        : String(user.creditLimit);

  return (
    <tr className={hasError ? "bg-red-50" : "hover:bg-gray-50"}>
      <td className="border border-gray-300 bg-gray-100 text-center px-2 py-1">
        {index + 1}
      </td>
      <EditableCell
        error={fieldErrors.loginId}
        value={user.loginId}
        onChange={(v) => onUpdate("loginId", v)}
      />
      <EditableCell
        error={fieldErrors.password}
        value={user.password}
        onChange={(v) => onUpdate("password", v)}
      />
      <EditableCell
        error={fieldErrors.userName}
        value={user.userName}
        onChange={(v) => onUpdate("userName", v)}
      />
      <EditableCell
        error={fieldErrors.email}
        value={user.email}
        onChange={(v) => onUpdate("email", v)}
        type="email"
      />
      <SelectCell
        error={fieldErrors.role}
        value={user.role || "一般ユーザー"}
        onChange={(v) => onUpdate("role", v)}
        options={VALID_ROLES}
      />
      <EditableCell
        error={fieldErrors.creditLimit}
        value={creditLimitDisplay}
        onChange={(v) => {
          const val = v.trim();
          onUpdate(
            "creditLimit",
            val === "" || val === "?" ? null : Number(val),
          );
        }}
        className="text-center"
        style={{ width: "120px" }}
      />
    </tr>
  );
};

/** エラーアイコン列の行 */
interface ErrorIconRowProps {
  user: BulkUserData;
  errorMessages: string[];
  isRegistered: boolean;
}

const ErrorIconRow: React.FC<ErrorIconRowProps> = ({
  user,
  errorMessages,
  isRegistered,
}) => {
  const tooltipId = `error-tooltip-${user.rowNumber}`;

  return (
    <div
      className="flex items-center justify-center"
      style={{ height: "33px" }}
    >
      {errorMessages.length > 0 ? (
        <>
          <Tooltip
            target={`#${tooltipId}`}
            position="right"
            className="red-tooltip"
          />
          <ErrorInfoIcon
            id={tooltipId}
            data-pr-tooltip={errorMessages.join("\n")}
          />
        </>
      ) : isRegistered ? (
        <CheckCircleIcon style={{ width: "1rem", height: "1rem" }} />
      ) : null}
    </div>
  );
};

// ============================================================
// メインコンポーネント
// ============================================================

interface BulkUserCreateModalProps {
  accountId: number;
  accountCreditLimit: number | null;
  existingAllocatedCredit: number;
  onClose: () => void;
  onProgressChange?: React.Dispatch<
    React.SetStateAction<BulkCreateProgress | null>
  >;
}

const BulkUserCreateModal: React.FC<BulkUserCreateModalProps> = ({
  accountId,
  accountCreditLimit,
  existingAllocatedCredit,
  onClose,
  onProgressChange,
}) => {
  useDisableBodyScroll();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // モーダルを開いた時点の既存クレジットを保持（親の更新を無視するため）
  const [initialExistingCredit] = useState(existingAllocatedCredit);

  // State
  const [isDragOver, setIsDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [parsedUsers, setParsedUsers] = useState<BulkUserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiValidationErrors, setApiValidationErrors] = useState<
    Map<number, ApiValidationErrors>
  >(new Map());
  const [apiLoginIdError, setApiLoginIdError] = useState<{
    loginId: string;
    error: string;
  } | null>(null);
  const [registeredIndices, setRegisteredIndices] = useState<Set<number>>(
    new Set(),
  );
  const [deleteFromRow, setDeleteFromRow] = useState<string>("");
  const [deleteToRow, setDeleteToRow] = useState<string>("");

  // Refs
  const errorIconsRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);

  // ============================================================
  // 状態管理
  // ============================================================

  const clearUsersState = () => {
    setParsedUsers([]);
    setErrorMessage(null);
    setApiValidationErrors(new Map());
    setApiLoginIdError(null);
    setRegisteredIndices(new Set());
    setDeleteFromRow("");
    setDeleteToRow("");
  };

  const updateUser = (
    rowNumber: number,
    field: keyof BulkUserData,
    value: string | number | null,
  ) => {
    setParsedUsers((prev) =>
      prev.map((user) =>
        user.rowNumber === rowNumber ? { ...user, [field]: value } : user,
      ),
    );
  };

  // ============================================================
  // 計算値（useMemo）
  // ============================================================

  const validationResults = useMemo(() => {
    const loginIdCounts = new Map<string, number>();
    parsedUsers.forEach((user) => {
      if (user.loginId) {
        loginIdCounts.set(
          user.loginId,
          (loginIdCounts.get(user.loginId) || 0) + 1,
        );
      }
    });

    const duplicateLoginIds = new Set<string>();
    loginIdCounts.forEach((count, loginId) => {
      if (count > 1) duplicateLoginIds.add(loginId);
    });

    const results = new Map<number, UserValidationErrors>();
    parsedUsers.forEach((user) => {
      results.set(user.rowNumber, validateUserData(user, duplicateLoginIds));
    });
    return results;
  }, [parsedUsers]);

  const errorCount = useMemo(() => {
    let count = 0;
    validationResults.forEach((errors) => {
      if (hasValidationErrors(errors)) count += 1;
    });
    return count;
  }, [validationResults]);

  // 登録済みユーザーと未登録ユーザーのクレジットを分けて計算
  const { registeredUsersCredit, unregisteredUsersCredit } = useMemo(() => {
    let registered = 0;
    let unregistered = 0;
    parsedUsers.forEach((user, index) => {
      if (
        user.creditLimit !== null &&
        !Number.isNaN(user.creditLimit) &&
        user.creditLimit > 0
      ) {
        if (registeredIndices.has(index)) {
          registered += user.creditLimit;
        } else {
          unregistered += user.creditLimit;
        }
      }
    });
    return {
      registeredUsersCredit: registered,
      unregisteredUsersCredit: unregistered,
      allParsedUsersCredit: registered + unregistered,
    };
  }, [parsedUsers, registeredIndices]);

  const allocationInfo = useMemo(() => {
    // 合計 = 既存（モーダル開いた時点の固定値） + 表の全ユーザーのクレジット
    const totalAllocated =
      initialExistingCredit + registeredUsersCredit + unregisteredUsersCredit;
    if (accountCreditLimit === null || accountCreditLimit === 0) {
      return { totalAllocated, percentage: null, isOverLimit: false };
    }
    const percentage = (totalAllocated / accountCreditLimit) * 100;
    return {
      totalAllocated,
      percentage,
      isOverLimit: totalAllocated > accountCreditLimit,
    };
  }, [
    initialExistingCredit,
    registeredUsersCredit,
    unregisteredUsersCredit,
    accountCreditLimit,
  ]);

  const hasApiValidationErrors = useMemo(() => {
    for (const [index, apiError] of apiValidationErrors.entries()) {
      const user = parsedUsers[index];
      if (!user) continue; // eslint-disable-line no-continue
      if (apiError.loginId?.value === user.loginId) return true;
      if (apiError.password?.value === user.password) return true;
      if (apiError.userName?.value === user.userName) return true;
      if (apiError.email?.value === user.email) return true;
      if (apiError.role?.value === user.role) return true;
      if (apiError.creditLimit?.value === user.creditLimit) return true;
    }
    return !!(
      apiLoginIdError &&
      parsedUsers.some((u) => u.loginId === apiLoginIdError.loginId)
    );
  }, [apiValidationErrors, apiLoginIdError, parsedUsers]);

  const isRegisterDisabled =
    errorCount > 0 ||
    hasApiValidationErrors ||
    allocationInfo.isOverLimit ||
    isLoading;

  // ============================================================
  // ファイル処理
  // ============================================================

  const isValidFile = (file: File): boolean =>
    ALLOWED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

  const processFile = async (file: File) => {
    clearUsersState();

    if (!isValidFile(file)) {
      setErrorMessage("xlsx または csv ファイルのみ対応しています");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(
        `ファイルサイズは${MAX_FILE_SIZE_MB}MB以下にしてください`,
      );
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      let result: { users: BulkUserData[]; headerRowIndex: number } | null =
        null;

      if (file.name.toLowerCase().endsWith(".csv")) {
        const decoder = new TextDecoder("utf-8", { fatal: true });
        const csvText = decoder.decode(arrayBuffer);
        const rows: (string | number | null)[][] = csvText
          .split("\n")
          .map((line) => line.split(",").map((cell) => cell.trim() || null));
        result = parseSheetData(rows);
      } else {
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        for (const sheetName of workbook.SheetNames) {
          const sheet = workbook.Sheets[sheetName];
          const rows: (string | number | null)[][] = XLSX.utils.sheet_to_json(
            sheet,
            {
              header: 1,
              defval: null,
            },
          );
          result = parseSheetData(rows);
          if (result) break;
        }
      }

      if (!result) {
        setErrorMessage(
          "ヘッダー行が見つかりませんでした。\nヘッダー行には「No, ログインID, パスワード, ユーザー名, メールアドレス, 権限, 個別上限」が必要です。",
        );
        return;
      }
      if (result.users.length === 0) {
        setErrorMessage("ユーザーデータが見つかりませんでした");
        return;
      }

      setParsedUsers(result.users);
    } catch {
      setErrorMessage(
        "ファイルの読み込みに失敗しました。\nファイルに問題がないかご確認のうえ再度お試しください。",
      );
    }
  };

  // ============================================================
  // イベントハンドラ
  // ============================================================

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files.length > 1) {
      setErrorMessage("1ファイルのみ選択してください");
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  };

  const handleDeleteRows = () => {
    const fromNo = parseInt(deleteFromRow, 10);
    const toNo = parseInt(deleteToRow, 10);

    if (
      Number.isNaN(fromNo) ||
      Number.isNaN(toNo) ||
      fromNo < 1 ||
      toNo < 1 ||
      fromNo > toNo
    ) {
      return;
    }

    const fromIndex = fromNo - 1;
    const toIndex = toNo - 1;

    const remainingUsers: { user: BulkUserData; oldIndex: number }[] = [];
    parsedUsers.forEach((user, index) => {
      if (index < fromIndex || index > toIndex) {
        remainingUsers.push({ user, oldIndex: index });
      }
    });

    const newRegisteredIndices = new Set<number>();
    remainingUsers.forEach(({ oldIndex }, newIndex) => {
      if (registeredIndices.has(oldIndex)) {
        newRegisteredIndices.add(newIndex);
      }
    });

    setParsedUsers(remainingUsers.map(({ user }) => user));
    setRegisteredIndices(newRegisteredIndices);
    setDeleteFromRow("");
    setDeleteToRow("");
  };

  const handleTableScroll = () => {
    if (tableBodyRef.current && errorIconsRef.current) {
      errorIconsRef.current.scrollTop = tableBodyRef.current.scrollTop;
    }
  };

  // ============================================================
  // 登録処理
  // ============================================================

  const handleRegister = async () => {
    if (errorCount > 0) return;

    setIsLoading(true);
    setErrorMessage(null);
    setApiValidationErrors(new Map());
    setApiLoginIdError(null);
    onProgressChange?.(null);

    try {
      const unregisteredUsers = parsedUsers
        .map((user, index) => ({ user, originalIndex: index }))
        .filter(({ originalIndex }) => !registeredIndices.has(originalIndex));

      if (unregisteredUsers.length === 0) {
        toast.success("すべてのユーザーは登録済みです");
        onClose();
        return;
      }

      const totalUsers = unregisteredUsers.length;
      const apiUsers = convertToApiUsers(
        unregisteredUsers.map(({ user }) => user),
      );

      // 1. バリデーション
      const validateResult = await dispatch(
        accountUsersActions.bulkValidateUsers({
          account_id: accountId,
          users: apiUsers,
        }),
      );

      if (
        !accountUsersActions.bulkValidateUsers.fulfilled.match(validateResult)
      ) {
        setErrorMessage(
          validateResult.error?.message || "バリデーションに失敗しました",
        );
        setIsLoading(false);
        return;
      }

      const { valid, failed_infos } = validateResult.payload;

      if (!valid) {
        setApiValidationErrors(
          buildValidationErrorMap(failed_infos, unregisteredUsers),
        );
        setErrorMessage(
          "登録に失敗しました。赤色でハイライトされている項目を修正してください。",
        );
        setIsLoading(false);
        return;
      }

      // 2. チャンク分割して登録
      const chunks = chunkArray(
        unregisteredUsers.map((item, idx) => ({
          ...apiUsers[idx],
          originalIndex: item.originalIndex,
        })),
        CHUNK_SIZE,
      );
      let completedCount = 0;
      onProgressChange?.({ current: 0, total: totalUsers });

      for (const chunk of chunks) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const chunkApiUsers = chunk.map(({ originalIndex, ...rest }) => rest);

        // eslint-disable-next-line no-await-in-loop
        const createResult = await dispatch(
          accountUsersActions.bulkCreateUsers({
            account_id: accountId,
            users: chunkApiUsers,
          }),
        );

        if (
          !accountUsersActions.bulkCreateUsers.fulfilled.match(createResult)
        ) {
          const payload = createResult.payload as
            | {
                created_users: unknown[];
                failed_user: {
                  row_index: number;
                  user_name: string;
                  error: string;
                };
              }
            | undefined;

          if (payload?.failed_user) {
            const { failed_user, created_users } = payload;
            const failedOriginalIndex =
              chunk[failed_user.row_index]?.originalIndex;
            const noPrefix =
              failedOriginalIndex !== undefined
                ? `No.${failedOriginalIndex + 1} `
                : "";
            setErrorMessage(
              `${noPrefix}ログインID「${failed_user.user_name}」の登録でエラーが発生しました。\n【エラー内容】 ${failed_user.error}`,
            );
            setApiLoginIdError({
              loginId: failed_user.user_name,
              error: failed_user.error,
            });

            if (created_users.length > 0) {
              setRegisteredIndices((prev) => {
                const newSet = new Set(prev);
                for (let j = 0; j < created_users.length; j += 1) {
                  newSet.add(chunk[j].originalIndex);
                }
                return newSet;
              });
            }
          } else {
            setErrorMessage(
              createResult.error?.message || "登録エラーが発生しました。",
            );
          }
          setIsLoading(false);
          onProgressChange?.(null);
          return;
        }

        setRegisteredIndices((prev) => {
          const newSet = new Set(prev);
          chunk.forEach((item) => newSet.add(item.originalIndex));
          return newSet;
        });

        completedCount += chunk.length;
        onProgressChange?.({ current: completedCount, total: totalUsers });
      }

      onProgressChange?.(null);
      toast.success(`${totalUsers}件のユーザーを登録しました`);
      onClose();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "登録に失敗しました",
      );
      onProgressChange?.(null);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // レンダリング
  // ============================================================

  return (
    <>
      <style>{`
        .orange-tooltip .p-tooltip-text {
          background-color: rgb(249 115 22);
          color: white;
          white-space: pre-line;
        }
        .orange-tooltip .p-tooltip-arrow {
          border-bottom-color: rgb(249 115 22);
        }
        .red-tooltip .p-tooltip-text {
          background-color: rgb(220 38 38);
          color: white;
          white-space: pre-line;
        }
        .red-tooltip .p-tooltip-arrow {
          border-right-color: rgb(220 38 38);
        }
      `}</style>
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 100 }}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        />

        <div
          className="relative bg-white rounded-lg shadow-lg overflow-hidden"
          style={{
            maxHeight: "90vh",
            width: parsedUsers.length > 0 ? "90vw" : "auto",
            maxWidth: parsedUsers.length > 0 ? "90vw" : "40vw",
          }}
        >
          {/* タイトルバー */}
          <div className="flex items-center justify-between bg-[#5571ad] text-white px-6 py-3">
            <h3 className="text-2xl font-bold">一括ユーザー追加</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <CloseModalIcon className="w-6 h-6" />
            </button>
          </div>

          {/* コンテンツエリア */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "calc(90vh - 140px)" }}
          >
            {parsedUsers.length === 0 ? (
              /* ファイル選択画面 */
              <div className="flex flex-col h-full">
                <div
                  className="p-6 flex-1"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <label
                    htmlFor="bulk-user-file-input"
                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      isDragOver
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                    }`}
                    style={{ padding: "32px 64px" }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      id="bulk-user-file-input"
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <SelectFileIcon className="w-12 h-12 text-gray-500" />
                      <p className="text-sm text-gray-500">
                        <strong className="font-semibold text-blue-600">
                          ファイルを選択、またはドラッグ&ドロップ
                        </strong>
                      </p>
                      <p className="text-xs text-gray-500">
                        xlsx, csv が使用可能（1ファイルのみ・{MAX_FILE_SIZE_MB}
                        MBまで）
                      </p>
                    </div>
                  </label>
                </div>
                {errorMessage && (
                  <div className="flex items-center justify-center gap-1 text-red-600 px-6 pb-4">
                    <WarningIcon
                      style={{
                        width: "1.25rem",
                        height: "1.25rem",
                        fill: "#dc2626",
                        stroke: "#ffffff",
                        color: "#dc2626",
                      }}
                    />
                    <span
                      className="text-sm"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {errorMessage}
                    </span>
                  </div>
                )}
                <div className="flex justify-end items-center px-6 py-3 border-t bg-white">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("bulk-user-file-input")?.click()
                      }
                      className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      ファイルを選択
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ユーザーデータテーブル表示 */
              <div className="p-6">
                {/* クレジット割振状況 */}
                {accountCreditLimit !== null && accountCreditLimit > 0 && (
                  <>
                    <h4 className="text-lg font-bold mb-4">
                      クレジット割振状況
                    </h4>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <UserDashboardBox
                        title="クレジット上限"
                        mainText={accountCreditLimit.toLocaleString()}
                        subText="クレジット"
                        mainTextColor="#2563eb"
                      />
                      <UserDashboardBox
                        title="割振済みクレジット"
                        mainText={allocationInfo.totalAllocated.toLocaleString()}
                        subText={`（既存: ${(initialExistingCredit + registeredUsersCredit).toLocaleString()} + 新規: ${unregisteredUsersCredit.toLocaleString()}）`}
                        mainTextColor="#16a34a"
                      />
                      <UserDashboardBox
                        title="割振率"
                        mainText={
                          allocationInfo.percentage !== null
                            ? `${allocationInfo.percentage.toFixed(1)}%`
                            : "-"
                        }
                        subText={
                          allocationInfo.isOverLimit
                            ? "100%を超える設定はできません"
                            : ""
                        }
                        mainTextColor={
                          allocationInfo.isOverLimit ? "#ef4444" : "#f97316"
                        }
                        subTextColor="#ef4444"
                      />
                    </div>
                  </>
                )}

                {/* ユーザーリストヘッダー */}
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-bold">
                    ユーザーリスト（{parsedUsers.length}件）
                  </h4>
                  <div
                    className="flex items-center gap-2"
                    style={{ marginRight: "24px" }}
                  >
                    <span className="text-sm text-gray-600">行を削除：</span>
                    <input
                      type="number"
                      min="1"
                      value={deleteFromRow}
                      onChange={(e) => setDeleteFromRow(e.target.value)}
                      placeholder="開始"
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="text-gray-500">〜</span>
                    <input
                      type="number"
                      min="1"
                      value={deleteToRow}
                      onChange={(e) => setDeleteToRow(e.target.value)}
                      placeholder="終了"
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleDeleteRows}
                      disabled={
                        !deleteFromRow ||
                        !deleteToRow ||
                        parseInt(deleteFromRow, 10) > parseInt(deleteToRow, 10)
                      }
                      className={`px-3 py-1 text-sm rounded ${
                        deleteFromRow &&
                        deleteToRow &&
                        parseInt(deleteFromRow, 10) <= parseInt(deleteToRow, 10)
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      削除
                    </button>
                  </div>
                </div>

                {/* テーブル */}
                <div className="flex items-start">
                  {/* エラーアイコン列 */}
                  <div style={{ width: "24px" }}>
                    <div style={{ height: "41px" }} />
                    <div
                      ref={errorIconsRef}
                      className="overflow-hidden"
                      style={{ maxHeight: "calc(60vh - 41px)" }}
                    >
                      {parsedUsers.map((user, index) => {
                        const fieldErrors = getFieldErrors(
                          validationResults.get(user.rowNumber),
                          apiValidationErrors.get(index),
                          apiLoginIdError,
                          user,
                        );
                        return (
                          <ErrorIconRow
                            key={user.rowNumber}
                            user={user}
                            errorMessages={buildErrorMessages(fieldErrors)}
                            isRegistered={registeredIndices.has(index)}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* テーブル本体 */}
                  <div className="flex-1 border border-gray-300">
                    <div
                      ref={tableBodyRef}
                      onScroll={handleTableScroll}
                      className="overflow-y-auto"
                      style={{ maxHeight: "60vh" }}
                    >
                      <table className="w-full border-collapse">
                        <thead
                          className="bg-[#5571ad]"
                          style={{ position: "sticky", top: 0, zIndex: 10 }}
                        >
                          <tr>
                            {Object.values(HEADER_COLUMNS).map((col) => (
                              <th
                                key={col.index}
                                className="px-3 py-2 text-center text-white border border-[#889bc5]"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <span>{col.label}</span>
                                  {col.icon}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {parsedUsers.map((user, index) => {
                            const errors = validationResults.get(
                              user.rowNumber,
                            );
                            const fieldErrors = getFieldErrors(
                              errors,
                              apiValidationErrors.get(index),
                              apiLoginIdError,
                              user,
                            );
                            const hasAnyError =
                              (errors && hasValidationErrors(errors)) ||
                              Object.values(fieldErrors).some(Boolean);

                            return (
                              <UserTableRow
                                key={user.rowNumber}
                                user={user}
                                index={index}
                                fieldErrors={fieldErrors}
                                hasError={hasAnyError}
                                onUpdate={(field, value) =>
                                  updateUser(user.rowNumber, field, value)
                                }
                              />
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div style={{ width: "24px" }} />
                </div>
              </div>
            )}
          </div>

          {/* ボトムバー */}
          {parsedUsers.length > 0 && (
            <div className="flex justify-between items-center px-6 py-3 border-t bg-white">
              <div>
                {errorMessage ? (
                  <div className="flex items-center gap-1 text-red-600">
                    <WarningIcon
                      style={{
                        width: "1.25rem",
                        height: "1.25rem",
                        fill: "#dc2626",
                        stroke: "#ffffff",
                        color: "#dc2626",
                      }}
                    />
                    <span className="text-sm">{errorMessage}</span>
                  </div>
                ) : errorCount > 0 ? (
                  <div className="flex items-center gap-1 text-red-600">
                    <WarningIcon
                      style={{
                        width: "1.25rem",
                        height: "1.25rem",
                        fill: "#dc2626",
                        stroke: "#ffffff",
                        color: "#dc2626",
                      }}
                    />
                    <span className="text-sm">
                      {errorCount}
                      件のエラーがあります。赤色でハイライトされている項目を修正してください。
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Tooltip
                      target="#time-info-tooltip"
                      position="top"
                      className="orange-tooltip"
                    />
                    <InfoIcon
                      id="time-info-tooltip"
                      data-pr-tooltip={`ユーザー1件あたり${SECONDS_PER_USER}秒ほどかかります`}
                      className="w-4 h-4 text-gray-700 cursor-help"
                    />
                    <span className="text-sm text-gray-500">
                      登録にかかる時間の目安：
                      <span className="font-bold">
                        約
                        {Math.ceil(
                          (parsedUsers.length - registeredIndices.size) *
                            SECONDS_PER_USER,
                        )}
                        秒
                      </span>
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  disabled={isRegisterDisabled}
                  onClick={handleRegister}
                  className={`px-6 py-2 text-white rounded-md ${
                    isRegisterDisabled
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  登録
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BulkUserCreateModal;
